import React, { useState } from 'react';
import {
  Table,
  Col,
  Row,
  Button,
  Input,
  Modal,
  FormGroup,
  Container,
  Badge,
} from 'reactstrap';
import { reduxForm } from 'redux-form';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import CloseCircleOutline from 'mdi-react/CloseCircleOutlineIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import AccountSearchIcon from 'mdi-react/AccountSearchIcon';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import Api from '../../../util/api';
import t, { partial } from '../../../util/translation/translation';
import ContactInfoForm from './ContactInfoForm';
import { extractPhoneValue } from '../../log_in/components/CountryPhone';
import useModal from '../../../shared/hooks/useModal';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';

const s = partial('shared');
const p = partial('AddEmployee');

const AVATAR_COLORS = [
  '#3B82F6',
  '#22C55E',
  '#A855F7',
  '#F97316',
  '#EC4899',
  '#14B8A6',
];

const avatarColorFor = (name) => {
  const str = name || '';
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const initialsFor = (firstName, lastName) =>
  `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';

const AddEmployee = (props) => {
  const [loading, setLoading] = useState(false);
  const [user, setUserData] = useState(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const [handleModal, visible, deleteID, comment, setComment] = useModal();

  const handelDeleteEmployee = () => {
    props.handleDeleteButton(deleteID, comment);
    handleModal();
  };
  const handleDeleteClick = (index) => {
    handleModal(index);
  };

  const handleCancelEdit = () => {
    setUserData({});
    setIsEdit(false);
  };

  const handleQuickPhoneSearch = (foundUser) => {
    setUserData({
      userCloudId: foundUser.userCloudId,
      firstname: foundUser.firstname,
      lastname: foundUser.lastname,
      email: foundUser.email,
      phoneNumber: foundUser.phoneNumber,
    });
  };

  const getEmployeeInformationForEdit = async (employeeID) => {
    try {
      if (employeeID) {
        const resEmployee = await Api.employees.getEmployeeById(employeeID);
        setUserData({
          userCloudId: resEmployee.id,
          firstname: resEmployee.firstName,
          lastname: resEmployee.lastName,
          email: resEmployee.email,
          phoneNumber: resEmployee.phone,
          companyPosition: resEmployee.positionTitle,
          visibleCompany: resEmployee.visibleCompany,
          visiblePrivate: resEmployee.visiblePrivate,
          isPrimaryContact:
            resEmployee.status === 'PRIMARY_CONTACT' ||
            resEmployee.status === 'PRIMARY_AND_VIP',
          isVIP:
            resEmployee.status === 'VIP' ||
            resEmployee.status === 'PRIMARY_AND_VIP',
        });
        setIsEdit(true);
      }
    } catch (err) {
      toast.error(err || `${p('employeeFetchError')}'!'`);
      setUserData({});
      setIsEdit(false);
    }
  };

  const handleEmployeeEditSubmit = async (values) => {
    const phone = extractPhoneValue(values.phone);

    let empStatus;

    if (values.isPrimaryContact && values.isVIP) {
      empStatus = 'PRIMARY_AND_VIP';
    } else if (values.isPrimaryContact) {
      empStatus = 'PRIMARY_CONTACT';
    } else if (values.isVIP) {
      empStatus = 'VIP';
    } else {
      empStatus = 'NONE';
    }
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone,
      positionTitle: values.companyPosition,
      visibleCompany: values.visibleCompany,
      visiblePrivate: values.visiblePrivate,
      status: empStatus,
    };
    try {
      setLoading(true);
      const employeeID = values.userId;
      if (employeeID) {
        await Api.employees.editEmployee(employeeID, payload);
        toast.success(`${p('employeeUpdated')}!`);
        setUserData({});
        setIsEdit(false);
        props.refecthEmployees();
      }
    } catch (err) {
      toast.error(err || `${p('employeeSavingERROR')}`);
    }
    setLoading(false);
  };

  const handleRecheck = async (employee) => {
    const phone = extractPhoneValue(employee?.phone);
    if (!phone || !isPossiblePhoneNumber(phone)) {
      toast.error(p('phoneNumberInfo'));
      return;
    }
    let empStatus;
    if (employee.isPrimaryContact && employee.isVIP) {
      empStatus = 'PRIMARY_AND_VIP';
    } else if (employee.isPrimaryContact) {
      empStatus = 'PRIMARY_CONTACT';
    } else if (employee.isVIP) {
      empStatus = 'VIP';
    } else {
      empStatus = 'NONE';
    }
    try {
      const foundUser = await Api.users.getUserByPhoneOrEmail(phone);
      if (!foundUser) {
        toast.info(p('noResultFound'));
        return;
      }
      await Api.employees.editEmployee(employee.id, {
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        phone,
        positionTitle: employee.positionTitle,
        visibleCompany: employee.visibleCompany,
        visiblePrivate: employee.visiblePrivate,
        status: empStatus,
        userId: foundUser.userCloudId,
      });
      toast.success(p('recheckUserFound'));
      props.refecthEmployees();
    } catch (err) {
      toast.error(err || `${p('employeeSavingERROR')}`);
    }
  };

  const connectedCount = props.employees.filter((e) => e?.userId).length;
  const waitingCount = props.employees.length - connectedCount;

  const renderEmployees = () => {
    return (
      props.employees &&
      props.employees.length > 0 &&
      props.employees.map((employee, index) => (
        <tr key={`employees-${employee.id}`}>
          <td>
            <div className="employee-cell">
              {employee?.profileImage ? (
                <img
                  className="company__profile__avatar"
                  src={employee?.profileImage}
                  alt="logo"
                />
              ) : (
                <span
                  className="company__profile__avatar_placeholder"
                  style={{
                    background: employee?.userId
                      ? avatarColorFor(
                          `${employee?.firstName || ''}${employee?.lastName || ''}`
                        )
                      : '#9CA3AF',
                  }}
                >
                  {initialsFor(employee?.firstName, employee?.lastName)}
                </span>
              )}
              <div>
                <strong>
                  {employee?.firstName} {employee?.lastName}
                </strong>
                <br />
                <small className="text-muted">{employee?.positionTitle}</small>
              </div>
            </div>
            <div className="marginTop-5">
              {employee?.userId ? (
                <Badge pill color="success">
                  <CheckIcon size={12} /> {p('connectedToApp')}
                </Badge>
              ) : (
                <Badge pill color="secondary">
                  <ClockOutlineIcon size={12} /> {p('awaitingLogin')}
                </Badge>
              )}
            </div>
          </td>
          {employee?.userId ? (
            <td>
              <Link to={`/users/edit/${employee?.userId}`}>
                {employee?.email}
              </Link>
            </td>
          ) : (
            <td>{employee?.email}</td>
          )}
          <td>{employee?.phone}</td>
          <td>
            <Badge
              pill
              color={employee?.visibleCompany ? 'success' : 'secondary'}
            >
              {employee?.visibleCompany ? s('visible') : p('hidden')}
            </Badge>
          </td>
          <td>
            <Badge
              pill
              color={employee?.visiblePrivate ? 'success' : 'secondary'}
            >
              {employee?.visiblePrivate ? s('visible') : p('hidden')}
            </Badge>
          </td>
          <td>
            <div className="status-dot-group">
              <span
                className={`status-dot ${employee.isPrimaryContact ? 'status-dot--on' : 'status-dot--off'}`}
              >
                {employee.isPrimaryContact ? <CheckIcon /> : <CloseIcon />}
              </span>
              /
              <span
                className={`status-dot ${employee.isVIP ? 'status-dot--on' : 'status-dot--off'}`}
              >
                {employee.isVIP ? <CheckIcon /> : <CloseIcon />}
              </span>
            </div>
          </td>
          <td>
            <div className="icon-actions">
              {!employee?.userId && (
                <SquareIconButton
                  icon={<AccountSearchIcon />}
                  onClick={() => handleRecheck(employee)}
                  title={p('recheckUser')}
                  // Temporarily disabled: editEmployee isn't persisting
                  // userId correctly on the backend yet.
                  disabled
                  id={`recheck-${employee.id}`}
                />
              )}
              <SquareIconButton
                icon={<EditOutlineIcon />}
                onClick={() => getEmployeeInformationForEdit(employee?.id)}
                title={s('edit')}
                id={`edit-${employee.id}`}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleDeleteClick(Number(index))}
                title={s('delete')}
                tone="danger"
                id={`delete-${employee.id}`}
              />
            </div>
          </td>
        </tr>
      ))
    );
  };

  return (
    <>
      <Container style={{ width: '100%' }}>
        <Loading loading={loading} />
        <div className="mb-3">
          <FormCard title={p('addAbusinessCardManually')}>
            <ContactInfoForm
              isEdit={isEdit}
              onSubmit={
                isEdit
                  ? handleEmployeeEditSubmit
                  : props.handleNewEmpolyeeSubmit
              }
              onCancel={handleCancelEdit}
              user={user}
              onUserFound={handleQuickPhoneSearch}
            />
          </FormCard>
        </div>
        {props.employees.length > 0 && (
          <Row className="my-4">
            <Col md={12}>
              <FormCard
                title={
                  <>
                    {s('employees')}{' '}
                    <span className="form-card__title-muted">
                      ({props.employees.length})
                    </span>
                  </>
                }
                actions={p('connectedSummary', {
                  connected: connectedCount,
                  waiting: waitingCount,
                })}
                bodyClassName="form-card__body--flush"
              >
                <Table responsive striped className="table--roomy">
                  <thead>
                    <tr>
                      <th>{s('name')}</th>
                      <th>{s('email')}</th>
                      <th>{s('phone')}</th>
                      <th>{t('Reports.visibleForPartners')}</th>
                      <th>{t('Reports.visibleForPublic')}</th>
                      <th>{t('AddEmployee.primaryContact')} / VIP</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>{renderEmployees()}</tbody>
                </Table>
                <div className="form-card__footnote">
                  <InformationOutlineIcon size={16} />
                  <span>{p('connectedBadgeExplanation')}</span>
                </div>
              </FormCard>
            </Col>
          </Row>
        )}
        <Modal
          isOpen={visible}
          toggle={() => {
            handleModal();
          }}
          size="md"
        >
          <div className="modal">
            <div className="modal-container" style={{ width: '500px' }}>
              <div className="modal-icon">
                <CloseCircleOutline size="36" color="#ff4861" />
              </div>
              <div className="modal-text">
                <h4>{p('deleteEmployeeText')}</h4>
              </div>
              <FormGroup style={{ width: '80%', marginTop: '5px' }}>
                <Input
                  type="textarea"
                  name="textarea"
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Why you want to remove this employee?"
                  bsSize="sm"
                />
              </FormGroup>

              <div className="modal-buttons">
                <Button
                  className="marginBottom-0"
                  color="danger"
                  onClick={handelDeleteEmployee}
                >
                  {s('delete')}
                </Button>
                <Button
                  className="marginBottom-0"
                  color="secondary"
                  onClick={() => {
                    handleModal();
                  }}
                >
                  {s('close')}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
        <div style={{ margin: '100px' }} />
      </Container>
    </>
  );
};

AddEmployee.propTypes = {
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      positionTitle: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      userId: PropTypes.string,
    })
  ),
  refecthEmployees: PropTypes.func.isRequired,
  handleDeleteButton: PropTypes.func.isRequired,
  handleNewEmpolyeeSubmit: PropTypes.func.isRequired,
};

AddEmployee.defaultProps = {
  employees: [],
};

const reduxF = reduxForm({
  form: 'addEmployee',
});

export default reduxF(AddEmployee);
